/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react'

import Link from 'next/link'

import { useRouter } from 'next/router'

import clsx from 'clsx'

import { deleteCookie, getCookies, setCookie } from 'cookies-next'

import { useFormik } from 'formik'

import { type GetServerSideProps } from 'next'

import { useTranslations } from 'next-intl'

import FormButton from '@/components/Form/FormButton'
import FormGroup from '@/components/Form/FormGroup'
import FormInput from '@/components/Form/FormInput'
import FormText from '@/components/Form/FormText'
import FormTextRequire from '@/components/Form/FormTextRequire'
import FormView from '@/components/Form/FormView'
import { LoginSlider } from '@/components/LoginSlider'
import { SideBarBanner } from '@/components/SideBarBanner'
import { BannerType } from '@/constants'
import { useAuth } from '@/containers'
import { useCookie } from '@/hooks/useCookie'
import HeaderNoMenu from '@/layouts/HeaderNoMenu'
import { type BannerList } from '@/types'
import { api } from '@/utils'
import { setContext } from '@/utils/api-interceptors'
import { getBanner, getConfig } from '@/utils/endpoint'
import { translateSchema } from '@/utils/validations'



type OtpProps = {
  banner: BannerList[]
}

const Otp = ({ banner }: OtpProps) => {
  const t = useTranslations('Login')
  const [phoneNumber] = useCookie('phoneNumber', '')
  const [requestId, setRequestId] = useCookie('requestId', '')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [remainTime, setRemainTime] = useState(120)
  const { setUser, setToken } = useAuth()
  const router = useRouter()
  const timeRef = useRef<any>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const onSubmitFn = () => {
    setIsSubmitted(true)
  }

  useEffect(() => {
    if (!phoneNumber || phoneNumber === '') {
      router.push('/login')
    }
  }, [phoneNumber])

  useEffect(() => {
    return () => {
      deleteCookie('phoneNumber')
      deleteCookie('requestId')
    }
  }, [])

  const redirect = router?.query?.redirect || null

  const formik = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: translateSchema('otp', t),

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSubmit: async values => {
      setIsProcessing(true)

      const res = await api(`api/auth/login/otp`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
        data: {
          code: values.otp.trim(),
          requestId,
        },
      })

      if (res instanceof Error) {
        setErrorMessage(res.message)
        setIsProcessing(false)
        
return
      }

      setIsProcessing(false)
      setToken(res.data.token)
      setCookie('token', res.data.token, { maxAge: 2147483647 })
      setCookie('channel', res.data.profile.saleChannel, { maxAge: 2147483647 })
      setUser(res.data.profile)
      deleteCookie('phoneNumber')
      deleteCookie('requestId')
      const redirectUrl = router.query.redirect as string
      const voucher = router.query.voucher as string
      let nextUrl = ''

      if (redirect) {
        nextUrl += redirectUrl

        if (voucher) {
          nextUrl += `?voucher=${voucher}`
        }
      }

      const siteUrl =
        typeof window !== 'undefined' && window?.location.origin + nextUrl

      router.push(siteUrl as string)
    },
  })

  const handleResendOtp = async () => {
    setErrorMessage('')

    if (!window.grecaptcha) {
      console.error('reCAPTCHA not yet loaded')
      
return
    }

    const tokenReCaptcha = await window.grecaptcha.execute(
      process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string,
      { action: 'login' }
    )

    if (tokenReCaptcha) {
      api(`api/auth/login/request-otp`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_AUTH_API_URL,
        data: {
          phone: phoneNumber,
        },
      })
        .then(res => {
          setRequestId(res.data.requestId)
          clearInterval(timeRef.current)
          setRemainTime(120)
          timeRef.current = setInterval(() => {
            setRemainTime(prevTime => prevTime - 1)
          }, 1000)
        })
        .catch(error => {
          setErrorMessage(error.message)
        })
    } else {
      console.log('reCAPTCHA verification failed')
    }
  }

  useEffect(() => {
    timeRef.current = setInterval(() => {
      setRemainTime(prev => prev - 1)
    }, 1000)

    
return () => {
      clearInterval(timeRef.current)
    }
  }, [])

  useEffect(() => {
    if (remainTime === 0) {
      clearInterval(timeRef.current)
    }
  }, [remainTime])

  return (
    <>
      <style global jsx>{`
        body::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <HeaderNoMenu />
      <SideBarBanner className='top-[37%]'></SideBarBanner>
      <main>
        {' '}
        <div className='h-[calc(100vh - 129px)] m-auto flex max-w-[1200px] items-center gap-x-[210px] pt-[5px]'>
          <div className='flex w-full flex-2 flex-col'>
            <div className='mb-[16px] text-HeadlineLarge font-bold text-dark-400'>
              {t('verifyOtp')}
            </div>
            <div className=''>
              <FormView
                onSubmit={formik.handleSubmit}
                buttonClassName='mt-[24px]'
              >
                <FormGroup className='flex w-full flex-col'>
                  <FormText className='mb-[32px] text-left text-Headline font-normal text-grey-800'>
                    {t('otpSent')}{' '}
                    <span className='font-bold'>{phoneNumber}</span>
                  </FormText>
                  <FormInput
                    name='otp'
                    placeholder={t('otpPld')}
                    value={formik.values.otp.trim()}
                    isError={isSubmitted && formik.errors.otp}
                    onChange={e => {
                      formik.handleChange({
                        target: { value: e.target.value.trim(), name: 'otp' },
                      })
                      setErrorMessage('')
                    }}
                    alert
                    className={clsx(
                      formik.errors.otp && 'border border-red-600',
                      '  !h-12 w-[380px] min-w-[360px] rounded-[6px] border border-solid border-info-border text-left transition  placeholder:text-grey-900 hover:cursor-pointer'
                    )}
                  ></FormInput>
                  {isSubmitted && formik.errors.otp ? (
                    <FormTextRequire className='pt-2 text-fieldInput text-sm font-normal text-primary-base'>
                      {formik.errors.otp}
                    </FormTextRequire>
                  ) : (
                    ''
                  )}
                  {errorMessage && isSubmitted ? (
                    <FormTextRequire className='pt-2 text-fieldInput text-sm font-normal text-primary-base'>
                      {errorMessage}
                    </FormTextRequire>
                  ) : (
                    <></>
                  )}
                </FormGroup>
                <FormButton
                  isProcessing={isProcessing}
                  className={clsx(
                    formik.dirty &&
                      ' !bg-primary-base text-Headline  !text-white-400',
                    'disabled:text-[rgb(0 0 0 / 25%)] h-[48px] w-full rounded border bg-grey p-[10px] font-semibold uppercase text-black-400/25 disabled:bg-grey disabled:text-black-400/25 disabled:outline-none'
                  )}
                  onClick={onSubmitFn}
                  disabled={!formik.dirty}
                >
                  {t('confirm')}
                </FormButton>
              </FormView>
            </div>
            {remainTime === 0 ? (
              <span className='mt-[32px] text-center  text-fieldLabel'>
                {' '}
                <span className=' text-dark-400'> {t('dontReceive')} </span>
                <span
                  className='cursor-pointer text-fieldLabel text-blue'
                  onClick={handleResendOtp}
                >
                  {t('resend')}
                </span>
              </span>
            ) : (
              <div className='mt-[32px] text-center'>
                {t('resendDesc')}{' '}
                <span>
                  {remainTime} {t('second')}
                </span>
              </div>
            )}
            <span className='mt-[24px] text-center'>
              <Link
                className='text-fieldLabel font-normal text-blue'
                href='/login'
              >
                {t('changePhone')}
              </Link>
            </span>
          </div>
          <div className='ml-auto min-h-[700px] min-w-[538px] py-[60px]'>
            <LoginSlider
              sliders={
                banner.find((e: BannerList) => e.type === BannerType.LOGIN)
                  ?.items
              }
            ></LoginSlider>
          </div>
        </div>
      </main>
    </>
  )
}

export default Otp

export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  locale,
}) => {
  const { language, messages } = await setContext(getCookies({ req, res }), {
    language: locale,
  })

  const [banner, systemConfig] = await Promise.all([getBanner(), getConfig()])

  return {
    props: {
      messages,
      banner,
      language,
      systemConfig,
    },
  }
}
